/**
 * useAuth Hook - FIXED to prevent infinite requests
 * Enhanced with employee preview mode support
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import authService from '../services/authService.js';

// Query Keys
const QUERY_KEYS = {
  AUTH: ['auth'],
  STORES: ['auth', 'stores']
};

// Helper to build fresh auth data snapshot from authService
const getFreshAuthData = () => ({
  user: authService.getCurrentUser(),
  company: authService.getCurrentCompany(),
  store: authService.getCurrentStore(),
  availableStores: authService.getAvailableStores(),
  isAuthenticated: authService.isAuthenticated(),
  isFullyAuthenticated: authService.isFullyAuthenticated()
});

export const useAuth = () => {
  const queryClient = useQueryClient();

  // Main auth state query - FIXED with proper caching
  const {
    data: authData,
    isLoading: isAuthLoading,
    error: authError,
    isSuccess: isAuthSuccess
  } = useQuery({
    queryKey: QUERY_KEYS.AUTH,
    queryFn: async () => {
      await authService.initialize();
      return getFreshAuthData();
    },
    staleTime: Infinity, // Never go stale
    cacheTime: Infinity, // Keep forever
    refetchOnMount: false, // CRITICAL: Don't refetch on mount
    refetchOnWindowFocus: false, // CRITICAL: Don't refetch on focus
    refetchOnReconnect: false, // Don't refetch on reconnect
    retry: false // Don't retry failed queries
  });

  // FIXED: Memoize derived state to prevent re-renders
  const derivedState = useMemo(() => {
    const user = authData?.user || null;
    const company = authData?.company || null;
    const store = authData?.store || null;
    const availableStores = authData?.availableStores || [];
    const isAuthenticated = authData?.isAuthenticated || false;
    const isFullyAuthenticated = authData?.isFullyAuthenticated || false;
    const isEmployeePreview = user?.userTypeId === 1 || user?.userTypeId === 3;

    return {
      user,
      company,
      store,
      availableStores,
      isAuthenticated,
      isFullyAuthenticated,
      isEmployeePreview
    };
  }, [authData]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (credentials) => authService.login(credentials),
    onSuccess: () => {
      queryClient.setQueryData(QUERY_KEYS.AUTH, getFreshAuthData());
      queryClient.invalidateQueries(['cart']);
    }
  });

  // Store selection mutation (customers)
  const selectStoreMutation = useMutation({
    mutationFn: (store) => authService.selectStore(store),
    onSuccess: () => {
      queryClient.setQueryData(QUERY_KEYS.AUTH, getFreshAuthData());
      queryClient.invalidateQueries(['cart']);
    }
  });

  // Company switch mutation (employees)
  const switchCompanyMutation = useMutation({
    mutationFn: (company) => {
      const success = authService.switchCompany(company);
      if (!success) throw new Error('Failed to switch company');
      return success;
    },
    onSuccess: () => {
      queryClient.setQueryData(QUERY_KEYS.AUTH, getFreshAuthData());
      // Invalidate all product-related queries since products are company-scoped
      queryClient.removeQueries({ queryKey: ['products'] });
      queryClient.removeQueries({ queryKey: ['product'] });
      queryClient.removeQueries({ queryKey: ['categories'] });
      queryClient.removeQueries({ queryKey: ['category'] });
      queryClient.removeQueries({ queryKey: ['brands'] });
      queryClient.removeQueries({ queryKey: ['brand'] });
    }
  });

  // FIXED: Stable callback functions
  const logout = useCallback(() => {
    authService.signOut(); // KAN-145: server-side token invalidation + local clear

    const emptyAuthData = {
      user: null,
      company: null,
      store: null,
      availableStores: [],
      isAuthenticated: false,
      isFullyAuthenticated: false
    };

    queryClient.setQueryData(QUERY_KEYS.AUTH, emptyAuthData);
    queryClient.clear();
  }, [queryClient]);

  // FIXED: Stable helper functions with proper dependencies
  const hasMultipleStores = useCallback(() => {
    return derivedState.availableStores.length > 1;
  }, [derivedState.availableStores.length]);

  const hasStoreAccess = useCallback(() => {
    if (derivedState.isEmployeePreview) {
      return (derivedState.user?.companies?.length || 0) > 0;
    }
    return derivedState.availableStores.length > 0;
  }, [derivedState.availableStores.length, derivedState.isEmployeePreview, derivedState.user?.companies?.length]);

  const needsStoreSelection = useCallback(() => {
    if (derivedState.isEmployeePreview) {
      // Employees with multiple companies and no company selected need selection
      return derivedState.isAuthenticated && !derivedState.company && (derivedState.user?.companies?.length || 0) > 0;
    }
    return derivedState.isAuthenticated && !derivedState.store && derivedState.availableStores.length > 0;
  }, [derivedState.isAuthenticated, derivedState.store, derivedState.availableStores.length, derivedState.isEmployeePreview, derivedState.company, derivedState.user?.companies?.length]);

  const canProceedToShopping = useCallback(() => {
    if (derivedState.isEmployeePreview) {
      return derivedState.isAuthenticated && !!derivedState.company;
    }
    return derivedState.isAuthenticated && !!derivedState.store;
  }, [derivedState.isAuthenticated, derivedState.store, derivedState.company, derivedState.isEmployeePreview]);

  const getStoreDisplayName = useCallback((storeToCheck = null) => {
    return authService.getStoreDisplayName(storeToCheck || derivedState.store);
  }, [derivedState.store]);

  const getCurrentStoreName = useCallback(() => {
    return derivedState.store?.name || 'No Store Selected';
  }, [derivedState.store?.name]);

  const getUserDisplayName = useCallback(() => {
    return authService.getUserDisplayName();
  }, [derivedState.user]);

  return {
    // State
    ...derivedState,
    isInitialized: isAuthSuccess,
    isLoading: isAuthLoading || loginMutation.isLoading || selectStoreMutation.isLoading || switchCompanyMutation.isLoading,
    error: authError || loginMutation.error || selectStoreMutation.error || switchCompanyMutation.error,

    // Actions
    login: loginMutation.mutateAsync,
    logout,
    selectStore: selectStoreMutation.mutateAsync,
    switchCompany: switchCompanyMutation.mutateAsync,

    // Loading states
    isLoggingIn: loginMutation.isLoading,
    isSelectingStore: selectStoreMutation.isLoading,
    isSwitchingCompany: switchCompanyMutation.isLoading,

    // Helper functions (stable)
    hasMultipleStores,
    hasStoreAccess,
    needsStoreSelection,
    canProceedToShopping,
    getStoreDisplayName,
    getCurrentStoreName,
    getUserDisplayName,

    // Pass-through service methods
    hasRole: authService.hasRole.bind(authService),
    hasClaim: authService.hasClaim.bind(authService),
    hasCompanyAccess: authService.hasCompanyAccess.bind(authService),
    getUserCompanies: authService.getUserCompanies.bind(authService),
    mustChangePassword: authService.mustChangePassword.bind(authService),
    isCustomer: authService.isCustomer.bind(authService),
    isEmployee: authService.isEmployee.bind(authService),
    isBrandUser: authService.isBrandUser.bind(authService),
    isUserType: authService.isUserType.bind(authService),
    hasMultipleCompanies: authService.hasMultipleCompanies.bind(authService)
  };
};

export default useAuth;
